import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { formatDateTime } from '@/lib/dateUtils'

function formatElapsedLong(startTime: string | null): string {
    if (!startTime) return '—'
    const start = new Date(startTime)
    if (isNaN(start.getTime())) return '—'
    const now = new Date()
    let sec = Math.max(0, Math.floor((now.getTime() - start.getTime()) / 1000))
    const totalMins = Math.floor(sec / 60)
    sec %= 60
    const totalHours = Math.floor(totalMins / 60)
    const minsRem = totalMins % 60
    const days = Math.floor(totalHours / 24)
    const hoursRem = totalHours % 24
    const parts: string[] = []
    if (days > 0) parts.push(`${days}d`)
    if (hoursRem > 0) parts.push(`${hoursRem}h`)
    if (minsRem > 0) parts.push(`${minsRem}m`)
    if (parts.length === 0) parts.push(sec > 0 ? `${sec}s` : '0m')
    return parts.join(' ')
}

interface ActiveIncidentBannerProps {
    incidentName: string
    startTime: string | null
    className?: string
}

export function ActiveIncidentBanner({ incidentName, startTime, className }: ActiveIncidentBannerProps) {
    const { t } = useTranslation()
    return (
        <div
            role="alert"
            className={cn(
                'flex items-center justify-between gap-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 dark:border-red-900/50 dark:bg-red-950/40',
                className
            )}
        >
            <div className="flex items-center gap-3">
                <span className="relative flex h-3 w-3">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-red-600 dark:bg-red-500" />
                </span>
                <span className="font-mono text-sm font-medium uppercase tracking-wider text-red-800 dark:text-red-200">
                    {t('recoverRequests.activeIncident', 'ACTIVE INCIDENT')}
                </span>
                <span className="text-sm text-red-900 dark:text-red-100">{incidentName}</span>
                {startTime && (
                    <span className="text-xs text-red-700 dark:text-red-300">
                        {t('recoverRequests.started', 'Started')} {formatDateTime(startTime)}
                    </span>
                )}
            </div>
            <span className="font-mono text-sm tabular-nums text-red-800 dark:text-red-200">
                {formatElapsedLong(startTime)}
            </span>
        </div>
    )
}
